import { useFormContext } from 'react-hook-form';
import { ActionDialog } from '@dx-ui/osc-dialog-v2';
import { FormInput } from '@dx-ui/osc-form';
import { Link, Text } from '@dx-ui/global-ui-prism';
import { Trans, useTranslation } from 'next-i18next';

import type { Dispatch, SetStateAction } from 'react';
import type { Brand_CountriesQuery } from './queries/generated/types';

type Props = {
  isUSA: boolean;
  isChina: boolean;
  selectedCountry: Brand_CountriesQuery['countries'][0];
  isMarketingOffersModalOpen: boolean;
  setIsMarketingOffersModalOpen: Dispatch<SetStateAction<boolean>>;
  language: string;
  origin: string;
};

export const MarketingContent = ({
  isUSA,
  isChina,
  selectedCountry,
  isMarketingOffersModalOpen,
  setIsMarketingOffersModalOpen,
  language,
  origin,
}: Props) => {
  const [t] = useTranslation('osc-join-form-join');
  const form = useFormContext();

  if (!isUSA) {
    if (selectedCountry?.marketingOptIn !== 'greenMarketingOptIn') {
      return (
        <>
          <FormInput
            name="marketingConsent"
            label={t('marketing.label')}
            type="checkbox"
            labelClassName="w-full"
            optional={true}
            aria-describedby="marketing"
          />
          <Text size="sm" id="marketing" className="!mt-0 ms-8">
            <Trans t={t} i18nKey={isChina ? 'marketing_china.agreement' : 'marketing.agreement'}>
              <Link url={`${origin}/${language}/p/global-privacy-statement/`} isNewWindow />
            </Trans>
          </Text>
          {/* Hide Offers checkbox - China only */}
          {!isChina ? (
            <>
              <FormInput
                name="marketingOffers"
                label={t('marketing_offers.label')}
                type="checkbox"
                labelClassName="w-full"
                optional={true}
                aria-describedby="marketing_offers"
                registerOptions={{
                  onChange: (e) => {
                    setIsMarketingOffersModalOpen(e.target.checked);
                  },
                }}
              />
              <Text size="sm" id="marketing_offers" className="!mt-0 ms-8">
                <Trans t={t} i18nKey="marketing_offers.agreement">
                  <Link url={`${origin}/${language}/p/global-privacy-statement/`} isNewWindow />
                </Trans>
              </Text>
              <FormInput
                name="marketingGrandVacations"
                label={t('marketing_grandVacations.label')}
                type="checkbox"
                labelClassName="w-full"
                optional={true}
                aria-describedby="marketing_grandVacations"
              />
              <Text size="sm" id="marketing_grandVacations" className="!mt-0 ms-8">
                <Trans t={t} i18nKey="marketing_grandVacations.agreement">
                  <Link
                    url="https://www.hiltongrandvacations.com/en/privacy-policy.html"
                    isNewWindow
                  />
                </Trans>
              </Text>
              <ActionDialog
                buttonOptions={{
                  cancel: {
                    label: t('dialog.decline'),
                  },
                  confirm: {
                    label: t('dialog.agree'),
                  },
                }}
                onConfirm={() => setIsMarketingOffersModalOpen(false)}
                onCancel={() => {
                  setIsMarketingOffersModalOpen(false);
                  form.setValue('marketingOffers', false);
                }}
                title={t('dialog.title')}
                isOpen={isMarketingOffersModalOpen}
                onDismiss={() => {
                  setIsMarketingOffersModalOpen(!isMarketingOffersModalOpen);
                  form.setValue('marketingOffers', false);
                }}
                size="lg"
              >
                <Text>{t('dialog.body')}</Text>
                <Text size="sm" className="my-4">
                  {t('dialog.disclaimer')}
                </Text>
              </ActionDialog>
            </>
          ) : null}
          {!isChina && <Text>{t('consent')}</Text>}
          <Text>
            <Trans t={t} i18nKey={isChina ? 'agreement_china' : 'agreement'}>
              <Link url={`/${language}/hilton-honors/terms/`} isNewWindow />
              <Link url={`${origin}/${language}/p/global-privacy-statement/`} isNewWindow />
            </Trans>
          </Text>
        </>
      );
    }
  }

  return (
    <>
      {selectedCountry?.marketingOptInForHGV && (
        <>
          <FormInput
            name="marketingGrandVacations"
            label={t('marketing_grandVacations.label')}
            type="checkbox"
            labelClassName="w-full"
            optional={true}
            aria-describedby="marketing_grandVacations"
          />
          <Text size="sm" id="marketing_grandVacations" className="!mt-0 ms-8">
            <Trans t={t} i18nKey="marketing_grandVacations.agreement">
              <Link url="https://www.hiltongrandvacations.com/en/privacy-policy.html" isNewWindow />
            </Trans>
          </Text>
          <Text>{t('consent')}</Text>
        </>
      )}
      <Text>
        <Trans t={t} i18nKey={isUSA ? 'agreement_US' : 'agreement'}>
          <Link url={`/${language}/hilton-honors/terms/`} isNewWindow />
          <Link url={`${origin}/${language}/p/global-privacy-statement/`} isNewWindow />
          <Link
            url={`${origin}/${language}/p/global-privacy-statement/#CaliforniaRights`}
            isNewWindow
          />
        </Trans>
      </Text>
    </>
  );
};
